import { useEffect, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { useAuth } from '@/features/auth/hooks/useAuth';
import { getDefaultRedirect } from '@/features/auth/context/AuthContext';
import {
  useConfirmEmail,
  useResendConfirmationEmail,
} from '@/features/email-verification/hooks/useEmailVerification';
import { needsEmailVerification, EMAIL_VERIFICATION_PATH } from '@/features/email-verification/utils/emailGate';

interface ApiErrorBody {
  code?: string;
  message?: string;
  meta?: Record<string, unknown>;
}

function extractApiError(err: unknown): ApiErrorBody {
  const error = (err as { response?: { data?: { error?: ApiErrorBody } } })?.response?.data?.error;
  return error ?? {};
}

/** Accepts only in-app paths, so a crafted state cannot bounce the user off-site. */
function safeRedirect(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  if (!value.startsWith('/') || value.startsWith('//')) return null;
  return value === EMAIL_VERIFICATION_PATH ? null : value;
}

const cardClass =
  'rounded-2xl border border-white/10 bg-white/[0.06] p-8 shadow-2xl backdrop-blur-xl sm:p-10';
const buttonClass =
  'mt-2 flex h-11 w-full items-center justify-center gap-2 rounded-lg font-medium text-white shadow-md transition-all duration-300 hover:shadow-lg hover:shadow-brand-teal/20 disabled:pointer-events-none disabled:opacity-60';
const buttonStyle = { background: 'linear-gradient(135deg, #1899AA 0%, #126f7d 100%)' };

/**
 * Doppio opt-in registrazione (2026-08-27, richiesta titolare): pagina che
 * gestisce sia l'attesa ("controlla la posta") sia il click sul link email
 * (?token=...). Sta prima del gate telefono in ProtectedRoute — l'OTP SMS
 * non è nemmeno richiedibile finché l'email non è confermata (gate anche
 * server-side, vedi phone-verification.service.ts).
 */
export function ConfermaEmailPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, isLoading, refreshUser, logout } = useAuth();

  const confirmMut = useConfirmEmail();
  const resendMut = useResendConfirmationEmail();
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const [resendIn, setResendIn] = useState(0);
  const [resendError, setResendError] = useState<string | null>(null);

  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setInterval(() => setResendIn((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [resendIn]);

  // Click sul link: conferma una sola volta per token, indipendentemente
  // dal fatto che questo browser abbia o meno una sessione attiva.
  useEffect(() => {
    if (!token || confirmed || confirmMut.isPending) return;
    confirmMut.mutate(token, {
      onSuccess: async () => {
        setConfirmed(true);
        if (isAuthenticated) await refreshUser();
      },
      onError: (err) => {
        setConfirmError(extractApiError(err).message || 'Link non valido o scaduto.');
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const target =
    safeRedirect((location.state as { redirectTo?: unknown } | null)?.redirectTo) ??
    (user ? getDefaultRedirect(user.role) : '/');

  const handleResend = async () => {
    setResendError(null);
    try {
      const res = await resendMut.mutateAsync();
      setResendIn(res.resendInSeconds);
    } catch (err) {
      const api = extractApiError(err);
      if (api.code === 'EMAIL_CONFIRMATION_COOLDOWN') {
        const secondsLeft = typeof api.meta?.secondsLeft === 'number' ? api.meta.secondsLeft : 60;
        setResendIn(secondsLeft);
        return;
      }
      setResendError(api.message || 'Invio fallito. Riprova.');
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/', { replace: true });
  };

  if (isLoading) return null;

  // Già confermata (o l'utente è arrivato qui per errore): via dal gate.
  if (isAuthenticated && user && !needsEmailVerification(user) && !token) {
    return <Navigate to={target} replace />;
  }

  return (
    <div
      className="relative flex min-h-screen items-center justify-center px-4 py-8"
      style={{
        background: 'linear-gradient(160deg, #231F55 0%, #1a1740 40%, #126f7d 100%)',
        paddingBottom: 'calc(2rem + var(--cookie-banner-h, 0px))',
      }}
    >
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse at 30% 20%, rgba(24,153,170,0.15) 0%, transparent 60%), radial-gradient(ellipse at 70% 80%, rgba(118,185,185,0.08) 0%, transparent 50%)',
        }}
      />

      <div className="relative z-10 w-full max-w-md">
        <div className="mb-8 flex justify-center">
          <img src="/images/logo-clean.png" alt="Klaro7" className="h-14 drop-shadow-lg" />
        </div>

        <div className={cardClass}>
          {token ? (
            confirmed ? (
              <div className="text-center">
                <h1 className="text-2xl font-semibold tracking-tight text-white">Email confermata</h1>
                <p className="mt-3 text-sm text-white/60">
                  {isAuthenticated
                    ? 'Ora verifichiamo il tuo numero di telefono.'
                    : 'Ora puoi accedere per continuare la registrazione.'}
                </p>
                {isAuthenticated ? (
                  <button type="button" onClick={() => navigate(target, { replace: true })} className={buttonClass} style={buttonStyle}>
                    <span className="text-sm">Continua</span>
                  </button>
                ) : (
                  <Link to="/login" className="mt-6 inline-block rounded-lg px-5 py-2.5 text-sm font-medium text-white shadow-md transition-all hover:shadow-lg" style={buttonStyle}>
                    Vai al login
                  </Link>
                )}
              </div>
            ) : confirmError ? (
              <div className="text-center">
                <h1 className="text-2xl font-semibold tracking-tight text-white">Link non valido</h1>
                <p className="mt-3 text-sm text-white/60">{confirmError}</p>
                {isAuthenticated && (
                  <button type="button" onClick={handleResend} disabled={resendMut.isPending || resendIn > 0} className={buttonClass} style={buttonStyle}>
                    <span className="text-sm">{resendIn > 0 ? `Rinvia tra ${resendIn}s` : 'Invia un nuovo link'}</span>
                  </button>
                )}
              </div>
            ) : (
              <p className="text-center text-sm text-white/60">Conferma in corso…</p>
            )
          ) : !isAuthenticated ? (
            <Navigate to="/login" replace />
          ) : (
            <div className="text-center">
              <h1 className="text-2xl font-semibold tracking-tight text-white">Controlla la tua email</h1>
              <p className="mt-3 text-sm text-white/60">
                Ti abbiamo inviato un link di conferma a <span className="font-medium text-white">{user?.email}</span>.
                Aprilo per continuare con la verifica del telefono.
              </p>

              {resendError && <p className="mt-4 text-sm text-red-300">{resendError}</p>}

              <button
                type="button"
                onClick={handleResend}
                disabled={resendMut.isPending || resendIn > 0}
                className={buttonClass}
                style={buttonStyle}
              >
                <span className="text-sm">{resendIn > 0 ? `Rinvia email tra ${resendIn}s` : 'Rinvia email'}</span>
              </button>

              <button
                type="button"
                onClick={handleLogout}
                className="mt-4 text-sm font-medium text-brand-teal transition-colors hover:text-brand-cyan"
              >
                Esci
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
